// Default roles, screens and menus as data (plan 7.1.2, 7.2.2, 7.2.3, 8.2.1, 8.2.3, 8.2.4).
// The server seeds `roles`/`role_permissions` from DEFAULT_ROLE_MATRIX; the web builds the rail/bottom tabs from
// menuFor(effectivePermissions) returned by GET /api/v1/auth/me (the server computes the same lists).
import { z } from 'zod';
import { BASE_ROLES, PRESET_ROLES, SYSTEM_ROLES, type SystemRole } from './enums.js';
import {
  LEVEL_RANK,
  PERMISSION_KEYS,
  type PermissionKey,
  type PermissionLevel,
  type PermissionMap,
  emptyPermissionMap,
  requiresMfa,
  requiresPledge,
  unionPermissions,
} from './permissions.js';

const build = (over: Partial<Record<PermissionKey, PermissionLevel>>, base: PermissionLevel = 'off'): PermissionMap => {
  const m = emptyPermissionMap();
  for (const k of PERMISSION_KEYS) m[k] = base;
  for (const [k, v] of Object.entries(over)) m[k as PermissionKey] = v as PermissionLevel;
  return m;
};

/**
 * Plan 7.2.2 default matrix — what each role ADDS ('—' = off). Everyone also holds `member`, so effective
 * permissions are unionPermissions([role, member]) (plan 7.1.3). Admin's `*`/`†` cells are still 'all': the fixed
 * control flow (7.5) and export controls (11.10) apply on top. DC-01 "separate admin from text reader" would turn
 * admin's 7 FLOW_KEYS off (not the default, D-003).
 */
export const DEFAULT_ROLE_MATRIX: Record<SystemRole, PermissionMap> = {
  admin: build({}, 'all'),
  manager: build({
    'usage.view': 'team',
    'cost.view': 'team',
    'stats.view': 'team',
    'logs.list': 'team',
    'people.view': 'team',
    'people.identity': 'team',
    'people.individual': 'team',
    'export.aggregate': 'team',
    'export.rows': 'team',
  }),
  member: build({
    'usage.view': 'self',
    'cost.view': 'self',
    'stats.view': 'self',
    'logs.list': 'self',
    'logs.prompt': 'self',
    'logs.answer': 'self',
    'logs.files': 'self',
    'logs.diff': 'self',
    'logs.commands': 'self',
    'logs.tool_output': 'self',
    'logs.raw': 'self',
    'export.aggregate': 'self',
    'export.rows': 'self',
    'export.identity': 'self',
    'export.content': 'self',
  }),
  content_reviewer: build({
    'logs.list': 'all',
    'people.identity': 'all',
    'logs.prompt': 'all',
    'logs.answer': 'all',
    'logs.files': 'all',
    'logs.diff': 'all',
    'logs.commands': 'all',
    'logs.tool_output': 'all',
    'logs.raw': 'all',
    'access.approve': 'all',
  }),
  log_reviewer: build({
    'usage.view': 'all',
    'cost.view': 'all',
    'stats.view': 'all',
    'logs.list': 'all',
    'people.view': 'all',
    'people.identity': 'all',
    'export.aggregate': 'all',
    'export.rows': 'all',
    'audit.view': 'all',
    'audit.verify': 'all',
  }),
  ops: build({
    'usage.view': 'all',
    'cost.view': 'all',
    'stats.view': 'all',
    'logs.list': 'all',
    'people.view': 'all',
    'people.identity': 'all',
    'logs.raw': 'all',
    'export.aggregate': 'all',
    'export.rows': 'all',
    'integrations.manage': 'all',
    'keys.view': 'all',
    'devices.revoke_any': 'all',
    'deletion.process': 'all',
    'retention.manage': 'all',
    'system.view': 'all',
  }),
  stats_viewer: build({
    'usage.view': 'all',
    'cost.view': 'all',
    'stats.view': 'all',
    'export.aggregate': 'all',
  }),
  delegated_admin: build({
    'usage.view': 'team',
    'cost.view': 'team',
    'stats.view': 'team',
    'logs.list': 'team',
    'people.view': 'team',
    'people.identity': 'team',
    'people.individual': 'team',
    'export.aggregate': 'team',
    'export.rows': 'team',
    'people.manage': 'team',
    'teams.manage': 'team',
    'roles.manage': 'team',
    'devices.revoke_any': 'team',
  }),
};

/** Plan 7.2.2 table 3 (key counts per role, MFA/pledge required) — kept as data so tests can check the matrix. */
export const ROLE_SUMMARY: Record<SystemRole, { keyCount: number; mfa: boolean; pledge: boolean }> = {
  admin: { keyCount: 35, mfa: true, pledge: true },
  manager: { keyCount: 9, mfa: true, pledge: true },
  member: { keyCount: 15, mfa: false, pledge: false },
  content_reviewer: { keyCount: 10, mfa: true, pledge: true },
  log_reviewer: { keyCount: 10, mfa: true, pledge: true },
  ops: { keyCount: 15, mfa: true, pledge: true },
  stats_viewer: { keyCount: 4, mfa: false, pledge: false },
  delegated_admin: { keyCount: 13, mfa: true, pledge: true },
};

export const ROLE_KINDS = ['base', 'preset', 'custom'] as const;
export const RoleKind = z.enum(ROLE_KINDS);
export type RoleKind = z.infer<typeof RoleKind>;

/** Static facts about the 8 system roles. Names are web i18n `role.<id>`; base roles are read-only (copy to edit). */
export const ROLE_DEFS = SYSTEM_ROLES.map((id) => ({
  id,
  kind: ((BASE_ROLES as readonly string[]).includes(id) ? 'base' : 'preset') as RoleKind,
  readOnly: (BASE_ROLES as readonly string[]).includes(id),
  /** Cannot be revoked; account suspension replaces it (plan 7.1.3 rule 1). */
  alwaysHeld: id === 'member',
  mfaRequired: requiresMfa(unionPermissions([DEFAULT_ROLE_MATRIX[id], DEFAULT_ROLE_MATRIX.member])),
  pledgeRequired: requiresPledge(unionPermissions([DEFAULT_ROLE_MATRIX[id], DEFAULT_ROLE_MATRIX.member])),
}));
export const PRESET_ROLE_IDS = PRESET_ROLES;

/** Effective permissions of someone holding `roles` (member is always added). */
export const effectivePermissionsOf = (roles: readonly SystemRole[]): PermissionMap =>
  unionPermissions([DEFAULT_ROLE_MATRIX.member, ...roles.map((r) => DEFAULT_ROLE_MATRIX[r])]);

export const countKeys = (m: PermissionMap): number => PERMISSION_KEYS.filter((k) => m[k] !== 'off').length;

// ───────────────────────── Screens (plan 8.2.1) ─────────────────────────

export const SCREEN_IDS = [
  'SCR-01',
  'SCR-02',
  'SCR-03',
  'SCR-04',
  'SCR-05',
  'SCR-06',
  'SCR-07',
  'SCR-08',
  'SCR-09',
  'SCR-10',
  'SCR-11',
  'SCR-12',
  'SCR-13',
  'SCR-14',
  'SCR-15',
] as const;
export const ScreenId = z.enum(SCREEN_IDS);
export type ScreenId = z.infer<typeof ScreenId>;

export const MENU_IDS = [
  'home',
  'logs',
  'stats',
  'people',
  'connect',
  'glossary',
  'settings',
  'admin/roles',
  'admin/policy',
  'admin/audit',
  'admin/system',
  'admin/cost',
] as const;
export const MenuId = z.enum(MENU_IDS);
export type MenuId = z.infer<typeof MenuId>;

/**
 * When a screen/menu opens (plan 7.2.3). `anyOf` = at least one key at `min` level or wider (default: any level).
 * `always` = every signed-in person (member role gives self scope to all of them).
 */
export interface OpenRule {
  always?: boolean;
  anyOf?: readonly { key: PermissionKey; min?: PermissionLevel }[];
}
export const canOpen = (rule: OpenRule, perms: PermissionMap): boolean =>
  !!rule.always ||
  !!rule.anyOf?.some(({ key, min }) => (min ? LEVEL_RANK[perms[key]] >= LEVEL_RANK[min] : perms[key] !== 'off'));

export interface ScreenDef {
  id: ScreenId;
  /** Web hash route; `:param` marks a path parameter. */
  route: string;
  menu: MenuId | null;
  /** First phase the screen is usable (plan 8.2.1 '주 단계'). */
  phase: Phase;
  open: OpenRule | 'public';
  /** Permission key whose scope drives the header view-scope selector on this screen (mockup route `scopePerm`). */
  scopeKey: PermissionKey | null;
  /** Hash query keys the screen reads (besides the common filter). */
  query: readonly string[];
}

export const PHASES = ['P0', 'P1', 'P2', 'P3', 'P4', 'P5'] as const;
export const Phase = z.enum(PHASES);
export type Phase = z.infer<typeof Phase>;

const ALWAYS: OpenRule = { always: true };
export const SCREENS: readonly ScreenDef[] = [
  { id: 'SCR-01', route: '#/login', menu: null, phase: 'P1', open: 'public', scopeKey: null, query: ['gerr'] },
  { id: 'SCR-02', route: '#/home', menu: 'home', phase: 'P2', open: ALWAYS, scopeKey: 'usage.view', query: [] },
  { id: 'SCR-03', route: '#/logs', menu: 'logs', phase: 'P2', open: { anyOf: [{ key: 'logs.list' }] }, scopeKey: 'logs.list', query: ['tool', 'model', 'surface', 'user', 'q', 'page'] },
  { id: 'SCR-04', route: '#/logs/:no', menu: 'logs', phase: 'P2', open: { anyOf: [{ key: 'logs.list' }] }, scopeKey: 'logs.list', query: ['tab', 'fv'] },
  { id: 'SCR-05', route: '#/stats', menu: 'stats', phase: 'P2', open: { anyOf: [{ key: 'stats.view' }] }, scopeKey: 'stats.view', query: ['item', 'view', 'tool', 'model', 'o.<card>.<option>'] },
  {
    id: 'SCR-06',
    route: '#/people',
    menu: 'people',
    phase: 'P3',
    open: { anyOf: [{ key: 'people.view' }, { key: 'stats.view', min: 'team' }] },
    scopeKey: 'usage.view',
    query: ['tab', 'team'],
  },
  { id: 'SCR-07', route: '#/people/:userId', menu: 'people', phase: 'P3', open: { anyOf: [{ key: 'people.individual' }] }, scopeKey: 'usage.view', query: [] },
  { id: 'SCR-08', route: '#/connect', menu: 'connect', phase: 'P1', open: ALWAYS, scopeKey: null, query: ['tab', 'cell'] },
  { id: 'SCR-09', route: '#/glossary/:slug?', menu: 'glossary', phase: 'P2', open: ALWAYS, scopeKey: null, query: ['q', 'cat', 'letter'] },
  { id: 'SCR-10', route: '#/settings', menu: 'settings', phase: 'P2', open: ALWAYS, scopeKey: null, query: ['tab'] },
  { id: 'SCR-11', route: '#/admin/roles', menu: 'admin/roles', phase: 'P3', open: { anyOf: [{ key: 'roles.manage' }, { key: 'access.approve', min: 'all' }, { key: 'logs.prompt', min: 'all' }, { key: 'logs.answer', min: 'all' }] }, scopeKey: null, query: ['tab'] },
  {
    id: 'SCR-12',
    route: '#/admin/policy',
    menu: 'admin/policy',
    phase: 'P3',
    open: {
      anyOf: [{ key: 'policy.manage' }, { key: 'notice.manage' }, { key: 'deletion.process' }, { key: 'legal_hold.manage' }, { key: 'retention.manage' }],
    },
    scopeKey: null,
    query: ['tab'],
  },
  { id: 'SCR-13', route: '#/admin/audit', menu: 'admin/audit', phase: 'P3', open: { anyOf: [{ key: 'audit.view' }] }, scopeKey: null, query: ['target'] },
  { id: 'SCR-14', route: '#/admin/system', menu: 'admin/system', phase: 'P3', open: { anyOf: [{ key: 'system.view' }] }, scopeKey: null, query: [] },
  { id: 'SCR-15', route: '#/admin/cost', menu: 'admin/cost', phase: 'P4', open: { anyOf: [{ key: 'policy.manage' }, { key: 'budget.manage' }] }, scopeKey: null, query: [] },
];

/** Rail order (plan 8.2.2) with the rule that shows each menu (same rules as the screens). */
export const MENU: readonly { id: MenuId; group: 'main' | 'admin'; screen: ScreenId }[] = [
  { id: 'home', group: 'main', screen: 'SCR-02' },
  { id: 'logs', group: 'main', screen: 'SCR-03' },
  { id: 'stats', group: 'main', screen: 'SCR-05' },
  { id: 'people', group: 'main', screen: 'SCR-06' },
  { id: 'connect', group: 'main', screen: 'SCR-08' },
  { id: 'glossary', group: 'main', screen: 'SCR-09' },
  { id: 'settings', group: 'main', screen: 'SCR-10' },
  { id: 'admin/roles', group: 'admin', screen: 'SCR-11' },
  { id: 'admin/policy', group: 'admin', screen: 'SCR-12' },
  { id: 'admin/audit', group: 'admin', screen: 'SCR-13' },
  { id: 'admin/system', group: 'admin', screen: 'SCR-14' },
  { id: 'admin/cost', group: 'admin', screen: 'SCR-15' },
];

const screenById = (id: ScreenId): ScreenDef => SCREENS.find((s) => s.id === id) as ScreenDef;
export const canOpenScreen = (id: ScreenId, perms: PermissionMap): boolean => {
  const rule = screenById(id).open;
  return rule === 'public' || canOpen(rule, perms);
};

/** Menus shown for these effective permissions (plan 8.2.3: admin 12 · manager 7 · member 6 …). */
export const menuFor = (perms: PermissionMap): MenuId[] => MENU.filter((m) => canOpenScreen(m.screen, perms)).map((m) => m.id);

/** Mobile bottom tabs (plan 8.2.4): 4 tabs + 'more'. People-menu roles get home·logs·people·stats. */
export const BOTTOM_TAB_MORE = 'more' as const;
export const bottomTabsFor = (menus: readonly MenuId[]): MenuId[] => {
  const want: MenuId[] = menus.includes('people') ? ['home', 'logs', 'people', 'stats'] : ['home', 'logs', 'stats', 'glossary'];
  const out = want.filter((id) => menus.includes(id));
  for (const id of ['glossary', 'connect', 'settings'] as const) if (out.length < 4 && !out.includes(id) && menus.includes(id)) out.push(id);
  return out;
};

/** Plan 8.2.3 expected menus per default role (tests compare menuFor(effective) to this). */
export const EXPECTED_MENUS: Record<SystemRole, readonly MenuId[]> = {
  admin: MENU_IDS,
  manager: ['home', 'logs', 'stats', 'people', 'connect', 'glossary', 'settings'],
  member: ['home', 'logs', 'stats', 'connect', 'glossary', 'settings'],
  content_reviewer: ['home', 'logs', 'stats', 'connect', 'glossary', 'settings', 'admin/roles'],
  log_reviewer: ['home', 'logs', 'stats', 'people', 'connect', 'glossary', 'settings', 'admin/audit'],
  ops: ['home', 'logs', 'stats', 'people', 'connect', 'glossary', 'settings', 'admin/policy', 'admin/system'],
  stats_viewer: ['home', 'logs', 'stats', 'people', 'connect', 'glossary', 'settings'],
  delegated_admin: ['home', 'logs', 'stats', 'people', 'connect', 'glossary', 'settings', 'admin/roles'],
};

/** Header view scope (URL `scope=`) and its mapping to the permission level it needs. */
export const VIEW_SCOPES = ['me', 'team', 'all'] as const;
export const ViewScope = z.enum(VIEW_SCOPES);
export type ViewScope = z.infer<typeof ViewScope>;
export const VIEW_SCOPE_LEVEL: Record<ViewScope, Exclude<PermissionLevel, 'off'>> = { me: 'self', team: 'team', all: 'all' };

/**
 * Scope choices offered in the header for a screen's scope key (plan 8.2.2/8.2.3): all → me/all (team filter inside
 * 'all'), team → me/team, self → me only.
 */
export const allowedViewScopes = (perms: PermissionMap, key: PermissionKey): ViewScope[] => {
  const lv = perms[key];
  if (lv === 'all') return ['me', 'all'];
  if (lv === 'team') return ['me', 'team'];
  return ['me'];
};

/** Tabs inside SCR-12 and the key that opens each (plan 7.2.3; ops sees the deletion tab only). */
export const POLICY_TABS = ['ceiling', 'notice', 'redaction', 'retention', 'access', 'deletion'] as const;
export const PolicyTab = z.enum(POLICY_TABS);
export type PolicyTab = z.infer<typeof PolicyTab>;
export const POLICY_TAB_RULE: Record<PolicyTab, OpenRule> = {
  ceiling: { anyOf: [{ key: 'policy.manage' }] },
  notice: { anyOf: [{ key: 'notice.manage' }, { key: 'policy.manage' }] },
  redaction: { anyOf: [{ key: 'policy.manage' }] },
  retention: { anyOf: [{ key: 'policy.manage' }] },
  access: { anyOf: [{ key: 'policy.manage' }] },
  deletion: { anyOf: [{ key: 'deletion.process' }, { key: 'legal_hold.manage' }, { key: 'retention.manage' }] },
};

/** Tabs inside SCR-06 (plan 7.2.3, mockup people.js gates). */
export const PEOPLE_TABS = ['teams', 'people', 'invite', 'service'] as const;
export const PeopleTab = z.enum(PEOPLE_TABS);
export type PeopleTab = z.infer<typeof PeopleTab>;
export const PEOPLE_TAB_RULE: Record<PeopleTab, OpenRule> = {
  teams: { anyOf: [{ key: 'stats.view', min: 'team' }] },
  people: { anyOf: [{ key: 'people.view' }] },
  invite: { anyOf: [{ key: 'people.manage' }] },
  service: { anyOf: [{ key: 'people.manage' }, { key: 'roles.manage' }] },
};

/** Tabs inside SCR-11 (plan 8.4.11). */
export const ROLES_TABS = ['matrix', 'grant', 'approvals', 'service'] as const;
export const RolesTab = z.enum(ROLES_TABS);
export type RolesTab = z.infer<typeof RolesTab>;

/** Tabs inside SCR-08 (plan 8.4.8). Team tab needs people.view (manager team / admin all). */
export const CONNECT_TABS = ['mine', 'new', 'app', 'team', 'support', 'coverage'] as const;
export const ConnectTab = z.enum(CONNECT_TABS);
export type ConnectTab = z.infer<typeof ConnectTab>;
export const CONNECT_TAB_RULE: Record<ConnectTab, OpenRule> = {
  mine: ALWAYS,
  new: ALWAYS,
  app: ALWAYS,
  team: { anyOf: [{ key: 'people.view' }] },
  support: ALWAYS,
  coverage: ALWAYS,
};

/** Tabs inside SCR-10 (plan 8.4.10). `views` also holds "my viewing activity" for FLOW_KEYS holders. */
export const SETTINGS_TABS = ['level', 'views', 'me'] as const;
export const SettingsTab = z.enum(SETTINGS_TABS);
export type SettingsTab = z.infer<typeof SettingsTab>;

/** Record detail tabs (plan 8.4.4). */
export const RECORD_TABS = ['qa', 'usage', 'actions', 'files', 'timeline'] as const;
export const RecordTab = z.enum(RECORD_TABS);
export type RecordTab = z.infer<typeof RecordTab>;

export const effectiveFor = (roles: readonly SystemRole[]): PermissionMap => effectivePermissionsOf(roles);
export const rankOf = (lv: PermissionLevel): number => LEVEL_RANK[lv];
